import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Checkbox,
  Input,
  VStack,
  Text,
  Box,
  IconButton,
  Collapse,
  Flex,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useForm, Controller } from 'react-hook-form';
import {
  updateTagWithTag,
  updateTagWithValue,
  removeWritable,
} from '../api/tag';
import { useToasts } from 'react-toast-notifications';
import { getApiErrorMessage } from '../utils/apiError';

const RWModalComponent = ({
  isOpen,
  onClose,
  tag,
  deviceOptions,
  onSuccess,
}) => {
  const { control, handleSubmit, reset, watch, setValue } = useForm();
  const [expandedDevice, setExpandedDevice] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);

  const isTagNameSelected = watch('isTagNameSelected', true);
  const isManualSelected = watch('isManualSelected', false);
  const { addToast } = useToasts();

  // Tag tiplerini ID'ye göre sınıflandırma
  const numberTypeIds = new Set([1, 2, 9]); // Ondalık sayı girişi için
  const stringTypeIds = new Set([21, 22, 23, 24, 25]); // String tipi için
  const integerTypeIds = new Set([
    3, 4, 5, 6, 7, 8, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
  ]); // Tam sayı girişi için

  useEffect(() => {
    if (isOpen) {
      reset({
        isTagNameSelected: true,
        isManualSelected: false,
        manualValue: '',
      });
      setExpandedDevice(null);
      setSelectedTag(null);
    }
  }, [isOpen, reset]);

  const handleSave = async data => {
    try {
      if (data.isDisableSelected) {
        await removeWritable(tag.registerId);
      } else if (data.manualValue && data.isManualSelected) {
        await updateTagWithValue(tag.registerId, data.manualValue);
      } else if (selectedTag && data.isTagNameSelected) {
        await updateTagWithTag(tag.registerId, selectedTag);
      } else {
        console.error('No valid input provided');
        return;
      }

      addToast('Tag updated successfully', { appearance: 'success' });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      addToast(getApiErrorMessage(error, 'Error updating tag'), {
        appearance: 'error',
      });
    }
  };

  const toggleDevice = deviceId => {
    setExpandedDevice(expandedDevice === deviceId ? null : deviceId);
  };

  const handleDisableCheckboxChange = field => {
    field.onChange(true);
    setValue('isTagNameSelected', false);
    setValue('isManualSelected', false);
    setValue('manualValue', '');
    setSelectedTag(null);
  };

  const handleTagSelect = (deviceId, tagId) => {
    setSelectedTag(tagId);
  };

  const handleTagNameCheckboxChange = field => {
    field.onChange(true);
    setValue('isManualSelected', false);
    setValue('isDisableSelected', false);
    setValue('manualValue', '');
    setSelectedTag(null);
  };

  const handleManualCheckboxChange = field => {
    field.onChange(true);
    setValue('isTagNameSelected', false);
    setValue('isDisableSelected', false);
    setSelectedTag(null);
  };

  // Aynı tipe sahip fakat farklı ID'li tag'ları filtreler
  const filterTagsByTypeAndDifferentId = deviceTags => {
    if (!tag) {
      return [];
    }

    const typeGroup = numberTypeIds.has(tag.type)
      ? numberTypeIds
      : stringTypeIds.has(tag.type)
        ? stringTypeIds
        : integerTypeIds;

    return deviceTags.filter(
      tagItem =>
        typeGroup.has(tagItem.type) && tagItem.registerId !== tag.registerId
    );
  };

  // Cihazları ve tag'ları filtreleme işlemi
  const filteredDeviceOptions = deviceOptions
    .map(device => {
      const filteredTags = filterTagsByTypeAndDifferentId(device.tags);
      return { ...device, tags: filteredTags };
    })
    .filter(device => device.tags.length > 0); // İçerisinde gösterilecek tag kalmayan cihazları eler

  // Tag tipine göre input tipini belirleme
  const isNumberType = numberTypeIds.has(tag?.type);
  const isStringType = stringTypeIds.has(tag?.type);
  const isIntegerType = integerTypeIds.has(tag?.type);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Modify Tag</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleSubmit(handleSave)}>
            <VStack align="start" spacing={2} w="100%">
              <Controller
                name="isTagNameSelected"
                control={control}
                defaultValue={true}
                render={({ field }) => (
                  <Checkbox
                    fontWeight="bold"
                    isChecked={field.value}
                    onChange={() => handleTagNameCheckboxChange(field)}
                  >
                    Tag Name
                  </Checkbox>
                )}
              />
              {isTagNameSelected && (
                <VStack align="start" spacing={1} w="100%">
                  {filteredDeviceOptions.map(device => (
                    <Box key={device.id} w="100%">
                      <Flex
                        align="center"
                        onClick={() => toggleDevice(device.id)}
                        cursor="pointer"
                      >
                        <IconButton
                          icon={
                            expandedDevice === device.id ? (
                              <ChevronDownIcon />
                            ) : (
                              <ChevronRightIcon />
                            )
                          }
                          size="sm"
                          variant="ghost"
                          aria-label="Expand/Collapse"
                        />
                        <Text>{device.deviceName}</Text>
                      </Flex>
                      <Collapse
                        in={expandedDevice === device.id}
                        animateOpacity
                      >
                        <Box
                          mt={2}
                          pl={4}
                          maxH="200px"
                          overflowY="scroll"
                          border="1px solid #ccc"
                          borderRadius="md"
                          p={2}
                        >
                          <VStack align="start">
                            {device.tags.map(tagItem => (
                              <Checkbox
                                key={tagItem.registerId}
                                isChecked={tagItem.registerId === selectedTag}
                                onChange={() =>
                                  handleTagSelect(device.id, tagItem.registerId)
                                }
                              >
                                {tagItem.name}
                              </Checkbox>
                            ))}
                          </VStack>
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                </VStack>
              )}
              <Controller
                name="isManualSelected"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <Checkbox
                    fontWeight="bold"
                    isChecked={field.value}
                    onChange={() => handleManualCheckboxChange(field)}
                  >
                    Manual
                  </Checkbox>
                )}
              />
              <Controller
                name="isDisableSelected"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <Checkbox
                    fontWeight="bold"
                    isChecked={field.value}
                    onChange={() => handleDisableCheckboxChange(field)}
                  >
                    Disable
                  </Checkbox>
                )}
              />

              {isManualSelected && (
                <Controller
                  name="manualValue"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Input
                      type={isStringType ? 'text' : 'number'} // String tipi için "text", diğerleri için "number"
                      step={isNumberType ? '0.01' : '1'} // Ondalık sayılar için 0.01 adım, tam sayılar için 1 adım
                      inputMode={
                        isIntegerType
                          ? 'numeric'
                          : isNumberType
                            ? 'decimal'
                            : 'text'
                      } // Tam sayılar için numeric, ondalık sayılar için decimal, string için text
                      pattern={isIntegerType ? '\\d*' : undefined} // Tam sayılar için regex pattern
                      placeholder={
                        isStringType
                          ? 'Input text'
                          : isNumberType
                            ? 'Enter number with decimals (e.g., 10.10)'
                            : 'Enter integer'
                      }
                      {...field}
                      mt={2}
                      onKeyDown={e => {
                        if (isIntegerType && (e.key === '.' || e.key === ',')) {
                          e.preventDefault();
                        }
                      }} // Tam sayı girişinde nokta veya virgül engellendi
                    />
                  )}
                />
              )}
            </VStack>
            <ModalFooter>
              <Button colorScheme="blue" type="submit">
                Save
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default RWModalComponent;
